import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LogoLockup, LogoMark } from "@/components/Logo";
import { ShieldCheck } from "lucide-react";

export default function DealerBio() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/dealer/${code}/widget`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [code]);

  return (
    <div className="min-h-screen bg-white text-[#0F1115] flex flex-col">
      <header className="border-b border-[var(--tm-border)]">
        <div className="tm-container flex items-center h-16">
          <Link to="/"><LogoLockup /></Link>
          <Link to="/" className="ml-auto text-sm text-[#3B424B] hover:text-[#0F1115]">Back to TreadMargin</Link>
        </div>
      </header>
      <main className="flex-1">
        <div className="tm-container py-16 max-w-2xl">
          {error ? (
            <div className="tm-card p-8 bg-white" data-testid="dealer-bio-notfound">
              <div className="text-2xl font-bold">Dealer badge not found</div>
              <p className="mt-3 text-sm text-[#3B424B]">The referral code <span className="font-mono">{code}</span> doesn't match an active TreadMargin dealer, or that dealer no longer subscribes.</p>
              <Link to="/" className="tm-btn-primary mt-6">Back to TreadMargin</Link>
            </div>
          ) : !data ? (
            <div className="text-sm text-[#3B424B]">Loading…</div>
          ) : (
            <>
              <div className="tm-demo-tag mb-4" data-testid="dealer-bio-tag">Verified independent tire dealer</div>
              <div className="flex items-start gap-5">
                <div className="rounded-xl border border-[var(--tm-border)] p-4 bg-[var(--tm-off)]"><LogoMark size={80} /></div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold" data-testid="dealer-bio-heading">TreadMargin Verified Dealer</h1>
                  <div className="mt-2 text-sm text-[#3B424B]">Dealer code <span className="font-mono">{data.org_code}</span>{data.province ? ` · ${data.province}` : ""}</div>
                </div>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <div className="tm-card p-5 bg-white" data-testid="dealer-bio-since">
                  <div className="text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold">Member since</div>
                  <div className="mt-2 text-xl font-bold font-mono">{data.member_since || "—"}</div>
                </div>
                <div className="tm-card p-5 bg-white" data-testid="dealer-bio-sku">
                  <div className="text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold">SKU depth</div>
                  <div className="mt-2 text-xl font-bold font-mono">{data.sku_bucket}</div>
                </div>
                <div className="tm-card p-5 bg-white" data-testid="dealer-bio-sales">
                  <div className="text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold">Sales history</div>
                  <div className="mt-2 text-xl font-bold font-mono">{data.sales_rows_bucket}</div>
                </div>
              </div>

              <div className="mt-8 tm-card p-6 bg-white">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="text-[#14624A] shrink-0 mt-0.5" size={20} />
                  <div>
                    <div className="font-semibold">What this badge means</div>
                    <p className="mt-2 text-sm text-[#3B424B]">This dealer runs their tire inventory and pricing decisions through TreadMargin — an intelligence layer that turns their own POS and inventory exports into actionable reorder, pricing and slow-inventory signals. TreadMargin never publishes a dealer's customer, revenue or supplier data. All counters above are anonymised buckets.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/" className="tm-btn-primary">Learn about TreadMargin</Link>
                <Link to="/early-access" className="tm-btn-outline">Are you a dealer? Join</Link>
              </div>
            </>
          )}
        </div>
      </main>
      <footer className="border-t border-[var(--tm-border)] bg-[var(--tm-off)]">
        <div className="tm-container py-8 text-xs text-[#3B424B] flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} TreadMargin · Inventory & pricing intelligence for independent tire dealers.</span>
          <Link to="/" className="hover:text-[#0F1115]">treadmargin.com</Link>
        </div>
      </footer>
    </div>
  );
}
